import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMapItems } from "../../hooks/maps/useMapItems";
import { useGroupedItems } from "../../hooks/maps/useGroupedItems";
import { useMarkerSync } from "../../hooks/maps/useMarkerSync";
import { useClusterer } from "../../hooks/maps/useClusterer";
import { ZoneLayer } from "./zones/ZoneLayer";
import { ItemGroupMarker } from "./markers/ItemGroupMarker";
import _ from "lodash";
import StackedItemsModal from "./StackedItemsModal";
import useCommonMap from "../../hooks/useCommonMap";

/**
 * @typedef {Object} NearbyItem
 * @property {AdvancedMarkerElement} marker
 * @property {HTMLDivElement}                           element
 * @property {ItemGroup}                                group
 */

/**
 * Displays nearby items onto map
 *
 * @param {boolean} showNearByAssets
 * @param {boolean} showNearByZones
 * @param {NearbyItem[]} nearbyItems
 * @param {Function} onCurrentChanged
 * @param {Number[]} clusteredItemIds
 * @param {Function} setClusteredItemIds
 */
const CommonMapNearByItems = ({
  showNearByAssets,
  showNearByZones,
  nearbyItems,
  onCurrentChanged,
  clusteredItemIds,
  setClusteredItemIds,
  children,
}) => {
  // const map = useMap();
  const pendingPortalUpdatesRef = useRef(new Map());
  const { map, selectedId, setSelectedId, setFollowing } = useCommonMap();

  // --- Data ---
  const { items, zones } = useMapItems(
    nearbyItems,
    showNearByAssets,
    showNearByZones
  );
  const groupedItems = useGroupedItems(items);

  // --- Selection state ---
  // const [selectedId, setSelectedId] = useState(null);
  const [stackedItems, setStackedItems] = useState([]);

  useEffect(() => {
    if (!selectedId) return;

    // Check if it's a zone or group of items
    const selectedZone = zones.find((_) => {
      return _.id === selectedId;
    });
    // console.log(`selected zone ${selectedZone?.id}`);
    const selectedGroup = groupedItems.get(selectedId);
    if (!selectedZone && !selectedGroup) return;

    if (selectedZone) {
      setFollowing(false);
      setStackedItems([]);
      onCurrentChanged({ current: selectedZone, marker: null });
    }

    if (selectedGroup) {
      if (selectedGroup.count === 1) {
        const item = selectedGroup.items[0];
        setStackedItems([]);
        onCurrentChanged({ current: item, marker: null });
        selectedGroup.showInfo = true;
      } else {
        setStackedItems(selectedGroup.items);
      }
    }
  }, [selectedId]);

  // --- Portal state ---
  const [portals, setPortals] = useState(new Map());

  const flushPortalUpdates = useCallback(() => {
    const updates = pendingPortalUpdatesRef.current;
    pendingPortalUpdatesRef.current = new Map();

    setPortals((prev) => {
      const next = new Map(prev);
      updates.forEach((portal, id) => {
        if (portal === null) {
          next.delete(id);
        } else {
          next.set(id, portal);
        }
      });
      return next;
    });
  }, []);

  const flushPortalUpdatesDebounce = useMemo(() => {
    return _.debounce(function () {
      flushPortalUpdates();
    }, 40);
  }, [flushPortalUpdates]);

  const schedulePortalFlush = useCallback(() => {
    flushPortalUpdatesDebounce();
  }, [flushPortalUpdatesDebounce]);

  const registerPortal = useCallback(
    (id, element, content) => {
      pendingPortalUpdatesRef.current.set(
        id,
        createPortal(content, element, id)
      );
      // Portal flush now handled by handleClustererRenderComplete
      // schedulePortalFlush();
    },
    [schedulePortalFlush]
  );

  const unregisterPortal = useCallback(
    (id) => {
      pendingPortalUpdatesRef.current.set(id, null);
      // Portal flush now handled by handleClustererRenderComplete
      // schedulePortalFlush();
    },
    [schedulePortalFlush]
  );

  // const registerPortal = useCallback(PortalHelper.registerPortal, []);
  // const unregisterPortal = useCallback(PortalHelper.unregisterPortal, []);

  // const unregisterPortal = useCallback((id) => {
  //   setPortals((prev) => {
  //     const next = new Map(prev);
  //     next.delete(id);
  //     return next;
  //   });
  // }, []);

  const handleClustererRenderComplete = useCallback(() => {
    schedulePortalFlush();
    setPortals((prev) => {
      return new Map(prev);
    });
  }, [schedulePortalFlush]);

  // --- Clusterer ---
  const { clustererRef, scheduleRender } = useClusterer(
    map,
    registerPortal,
    unregisterPortal,
    selectedId,
    setSelectedId,
    handleClustererRenderComplete
  );

  // --- Marker sync ---
  const { markerMapRef } = useMarkerSync(
    groupedItems,
    map,
    clustererRef.current,
    scheduleRender,
    (groupKey) => setSelectedId(groupKey)
  );

  // --- Context value ---
  // const contextValue = useMemo(() => {
  //   // console.log("context !!!!!!");
  //   return {
  //     registerPortal,
  //     unregisterPortal,
  //     selectedId,
  //     setSelectedId,
  //   };
  // }, [registerPortal, unregisterPortal, selectedId]);
  // if (loading && !data.length) return <div>Loading...</div>;
  // if (error) return <div>Error: {error}</div>;

  // useEffect(() => {
  //   if (!portals) return;
  //   console.log(portals.size.toString() + JSON.stringify([...portals.keys()]));
  // }, [portals]);

  // console.log("%c%s", "background: #fa4", portals.size.toString());
  // console.log("%c%s", "background: #fa4", selectedId);

  return (
    <>
      {/* <MapContext.Provider value={contextValue}> */}
      {/* Zones — polygon overlays, never clustered */}
      <ZoneLayer zones={zones} />

      {/* Item group markers — each registers a portal, renders nothing itself */}
      {[...markerMapRef.current.entries()].map(([groupKey, entry]) => (
        <ItemGroupMarker
          key={groupKey}
          groupKey={groupKey}
          markerEntry={entry}
          registerPortal={registerPortal}
          unregisterPortal={unregisterPortal}
        />
      ))}

      {/* All portals render here — item groups and clusters */}
      {[...portals.values()]}

      {/* </MapContext.Provider> */}

      <StackedItemsModal
        stackedItems={stackedItems}
        onHide={() => {
          setStackedItems([]);
          // setSelectedId(null);
        }}
      />
    </>
  );
};

export default CommonMapNearByItems;
